/**
 * プレビュー表示中の ⌥[ / ⌥]（および配列非依存の ⌥, / ⌥.）で、遷移元リスト
 * （やることホイール / HOME 完了タブ）の前後のショットへ直接移動する。
 *
 * - 並びは遷移元が location.state.previewQueue で渡す（Preview 側で再構築しない。
 *   完了タブのページネーション・タグ絞り込みとずれないようにするため）。
 * - 現在位置は shotIds と URL の shotId から毎回解決する。リスト外のショット
 *   （待機雲から開いた場合等）では何もしない。
 * - 端では止まる（プロジェクト切り替えと違い循環しない）。
 * - グローバルの ⌥[ / ⌥]（プロジェクト切り替え）はプレビュー表示中（URL が
 *   2 セグメント）には無効なので衝突しない（GlobalShortcutListener 参照）。
 * - 移動時の state は previewQueue だけにする（前のショットの shot データや
 *   sharedTransition を持ち越さない。ショット情報は Preview が API から引く）。
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getAltProjectDirection,
  isTypingTarget,
} from "../../../utils/keyboardShortcuts";
import type { PreviewQueue } from "../../../types/previewQueue";

export function usePreviewQueueShortcut(
  projectId: string | undefined,
  shotId: string | undefined,
): void {
  const navigate = useNavigate();
  const location = useLocation();
  const previewQueue =
    (location.state as { previewQueue?: PreviewQueue } | null)?.previewQueue ??
    null;

  // ハンドラは ref 経由で最新を参照し、listener の張り直しを避ける
  const ctxRef = useRef({ projectId, shotId, previewQueue });
  ctxRef.current = { projectId, shotId, previewQueue };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const ctx = ctxRef.current;
      if (!ctx.projectId || !ctx.shotId) return;
      const shotIds = ctx.previewQueue?.shotIds;
      if (!shotIds?.length) return;
      const direction = getAltProjectDirection(e);
      if (direction === null) return;
      if (isTypingTarget(e.target)) return;

      const currentIndex = shotIds.indexOf(ctx.shotId);
      if (currentIndex === -1) return;
      const nextIndex = currentIndex + direction;
      // 端では止まる
      if (nextIndex < 0 || nextIndex >= shotIds.length) return;

      e.preventDefault();
      navigate(`/${ctx.projectId}/${shotIds[nextIndex]}`, {
        state: { previewQueue: ctx.previewQueue },
      });
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);
}

export default usePreviewQueueShortcut;
